import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchUserInjuries,
  fetchInjuryById,
  addInjury as addInjuryAction,
  updateInjury as updateInjuryAction,
  deleteInjury as deleteInjuryAction,
  markRecovered as markRecoveredAction,
  clearError as clearErrorAction,
  selectInjuries,
  selectInjuredIntervals
} from '../store/slices/injurySlice';

export const useInjury = () => {
  const dispatch = useAppDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Get the injury state from Redux
  const injuries = useAppSelector(selectInjuries);
  const injuredIntervals = useAppSelector(selectInjuredIntervals);
  const currentInjury = useAppSelector((state) => state.injury?.currentInjury || null);
  const storeError = useAppSelector((state) => state.injury?.error || null);
  
  // Clear error
  const clearError = useCallback(() => {
    setError(null);
    dispatch(clearErrorAction());
  }, [dispatch]);
  
  // Get user's injuries
  const getUserInjuries = useCallback(async (userId: string) => {
    setIsLoading(true);
    setError(null);

    try {
      const resultAction = await dispatch(fetchUserInjuries(userId));
      const injuries = fetchUserInjuries.fulfilled.match(resultAction)
        ? resultAction.payload
        : [];

      setIsLoading(false);
      return injuries as any[]; // Explicit type to ensure it's an array
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to load injuries');
      return [] as any[]; // Explicit empty array with type
    }
  }, [dispatch]);
  
  // Get injury by ID
  const getInjuryById = useCallback(async (userId: string, injuryId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const resultAction = await dispatch(fetchInjuryById({ userId, injuryId }));
      const injury = fetchInjuryById.fulfilled.match(resultAction)
        ? resultAction.payload
        : null;
      
      setIsLoading(false);
      return injury;
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to load injury');
      return null;
    }
  }, [dispatch]);
  
  // Add a new injury
  const addInjury = useCallback(async (injuryData: any) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const resultAction = await dispatch(addInjuryAction(injuryData));
      const result = addInjuryAction.fulfilled.match(resultAction)
        ? resultAction.payload
        : null;
      
      setIsLoading(false);
      return result;
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to add injury');
      throw err;
    }
  }, [dispatch]);
  
  // Update an existing injury
  const updateInjury = useCallback(async (userId: string, injuryId: string, injuryData: any) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const resultAction = await dispatch(updateInjuryAction({ userId, id: injuryId, data: injuryData }));
      const result = updateInjuryAction.fulfilled.match(resultAction)
        ? resultAction.payload
        : null;
      
      setIsLoading(false);
      return result;
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to update injury');
      throw err;
    }
  }, [dispatch]);
  
  // Delete an injury
  const deleteInjury = useCallback(async (userId: string, injuryId: string) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const resultAction = await dispatch(deleteInjuryAction({ userId, injuryId }));
      const result = deleteInjuryAction.fulfilled.match(resultAction)
        ? resultAction.payload
        : null;
      
      setIsLoading(false);
      return result;
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to delete injury');
      throw err;
    }
  }, [dispatch]);
  
  // Mark an injury as recovered
  const markInjuryRecovered = useCallback(async (userId: string, injuryId: string, recoveryDate: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const resultAction = await dispatch(markRecoveredAction({ userId, id: injuryId, recoveryDate }));
      const result = markRecoveredAction.fulfilled.match(resultAction)
        ? resultAction.payload
        : null;
      
      setIsLoading(false);
      return result;
    } catch (err: any) {
      setIsLoading(false);
      setError(err.message || 'Failed to mark injury as recovered');
      throw err;
    }
  }, [dispatch]);
  
  return {
    // State
    injuries,
    currentInjury,
    isLoading,
    error: error || storeError,
    
    // Actions
    getUserInjuries,
    getInjuryById,
    addInjury,
    updateInjury,
    deleteInjury,
    markInjuryRecovered,
    clearError,
    injuredIntervals
  };
};

export default useInjury;
